import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { BASE_URL } from "./constants";
import Sidebar from "./Navbar";

const ProviderDetailsPage = () => {
  const { id, type } = useParams();
  const [providerData, setProviderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState("");
  const [actionSuccess, setActionSuccess] = useState("");
  const navigate = useNavigate();
  const [fullscreenImg, setFullscreenImg] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [imageLoading, setImageLoading] = useState({});
  const [providerIncome, setProviderIncome] = useState(null);
  const [providerIncomeLoading, setProviderIncomeLoading] = useState(false);
  const [incomeStartDate, setIncomeStartDate] = useState(() => {
    const start = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    return start.toISOString().split("T")[0];
  });
  const [incomeEndDate, setIncomeEndDate] = useState(() => {
    return new Date().toISOString().split("T")[0];
  });
  const admin = JSON.parse(localStorage.getItem("admin"));

  // Fetch provider income for a given date range (YYYY-MM-DD)
  const fetchProviderIncome = useCallback(
    async (startDate, endDate) => {
      setProviderIncomeLoading(true);
      try {
        const incomeResp = await fetch(
          `${BASE_URL}admin/account/providers/${id}/income?startDate=${startDate}&endDate=${endDate}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
        const incomeJson = await incomeResp.json();
        if (incomeJson && incomeJson.status === "success") {
          setProviderIncome(incomeJson.data);
        } else {
          setProviderIncome(null);
        }
      } catch (err) {
        console.error("Failed to fetch provider income", err);
        setProviderIncome(null);
      }
      setProviderIncomeLoading(false);
    },
    [id],
  );

  // Make fetchProviderDetails callable from UI (refresh images)
  const fetchProviderDetails = useCallback(async () => {
    setLoading(true);
    setActionError("");
    try {
      const response = await fetch(`${BASE_URL}admin/providers/${id}/details`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = await response.json();
      if (data.status === "success") {
        setProviderData(data.data);
        setImageErrors({});
        setImageLoading({
          profileImage: true,
          aadharImage: true,
          aadharBackImage: true,
          panImage: true,
        });

        // Fetch provider income/earnings for configured range
        fetchProviderIncome(incomeStartDate, incomeEndDate);
      } else {
        setActionError("Failed to fetch provider details");
      }
    } catch (err) {
      setActionError("Network error while fetching details");
      console.error("Fetch error:", err);
    }
    setLoading(false);
  }, [id, incomeStartDate, incomeEndDate, fetchProviderIncome]);

  useEffect(() => {
    fetchProviderDetails();
  }, [fetchProviderDetails]);

  const handleAction = async (actionType) => {
    setActionLoading(true);
    setActionError("");
    setActionSuccess("");
    let url = "";
    if (actionType === "verify") {
      url = `${BASE_URL}admin/providers/${id}/verify`;
    } else if (actionType === "suspend") {
      url = `${BASE_URL}admin/providers/${id}/suspend`;
    } else if (actionType === "unsuspend") {
      url = `${BASE_URL}admin/providers/${id}/unsuspend`;
    }
    try {
      const res = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = await res.json();
      if (data.status === "success") {
        setActionSuccess(data.message || "Action successful");
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        setActionError(data.message || "Action failed");
      }
    } catch (err) {
      setActionError("Network error");
    }
    setActionLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("admin");
    navigate("/login");
  };

  const handleImageLoad = (key) => {
    setImageLoading((prev) => ({ ...prev, [key]: false }));
  };

  const handleImageError = (key) => {
    setImageErrors((prev) => ({ ...prev, [key]: true }));
    setImageLoading((prev) => ({ ...prev, [key]: false }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex">
        <Sidebar admin={admin} onLogout={handleLogout} />
        <main className="flex-1 lg:ml-72 min-h-screen flex items-center justify-center">
          <div className="text-center p-8 bg-white rounded-2xl border border-stone-200 shadow-sm max-w-sm w-full mx-4">
            <div className="w-12 h-12 border-4 border-[#F8D346] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <h3 className="text-lg font-bold text-stone-900 mb-1">
              Loading Buddy Profile
            </h3>
            <p className="text-sm text-stone-500">
              Fetching verified records & performance metrics...
            </p>
          </div>
        </main>
      </div>
    );
  }

  if (!providerData) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex">
        <Sidebar admin={admin} onLogout={handleLogout} />
        <main className="flex-1 lg:ml-72 min-h-screen flex items-center justify-center p-4">
          <div className="text-center max-w-md w-full bg-white rounded-2xl border border-stone-200 p-8 shadow-sm">
            <div className="w-16 h-16 bg-rose-50 text-rose-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-stone-900 mb-2">
              Provider Record Not Found
            </h3>
            <p className="text-sm text-stone-500 mb-6">
              The requested service provider ID could not be retrieved from the
              database.
            </p>
            <button
              onClick={() =>
                navigate(
                  type === "non-verified"
                    ? "/admin/unverified-buddies"
                    : "/admin/verified-buddies",
                )
              }
              className="px-6 py-2.5 bg-stone-900 hover:bg-stone-800 text-white rounded-xl text-sm font-semibold transition-all shadow-sm"
            >
              Back to{" "}
              {type === "non-verified"
                ? "Pending Verification"
                : "Verified Buddies"}
            </button>
          </div>
        </main>
      </div>
    );
  }

  const { personalInfo, statusInfo, documents, subServices, statistics } =
    providerData;

  const isUnverified =
    type === "non-verified" || !statusInfo?.isVerified;
  const backPath = isUnverified
    ? "/admin/unverified-buddies"
    : "/admin/verified-buddies";
  const backLabel = isUnverified
    ? "Pending Verification"
    : "Verified Buddies";

  const getInitials = (name) => {
    if (!name) return "P";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex">
      <Sidebar admin={admin} onLogout={handleLogout} />

      {/* Main Content Area */}
      <main className="flex-1 lg:ml-72 min-h-screen flex flex-col">
        {/* Sticky Executive Topbar */}
        <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-stone-200/80 px-4 sm:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(backPath)}
                className="w-10 h-10 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-700 flex items-center justify-center transition-colors shadow-sm"
                title={`Back to ${backLabel}`}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <div>
                <div className="flex items-center gap-2 text-xs font-semibold text-stone-400">
                  <Link
                    to={backPath}
                    className="hover:text-stone-700 transition-colors"
                  >
                    {backLabel}
                  </Link>
                  <span>/</span>
                  <span className="text-stone-700">Dossier</span>
                </div>
                <h1 className="text-xl font-extrabold text-stone-900 tracking-tight flex items-center gap-2">
                  <span>{personalInfo.name}</span>
                  {statusInfo.isVerified && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                      <svg
                        className="w-3 h-3 text-emerald-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Verified Buddy
                    </span>
                  )}
                  {statusInfo.isSuspended && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-rose-100 text-rose-800 border border-rose-200">
                      Restricted
                    </span>
                  )}
                </h1>
              </div>
            </div>

            {/* Header Action Buttons */}
            <div className="flex items-center gap-2.5">
              {!statusInfo.isVerified && (
                <button
                  onClick={() => handleAction("verify")}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-sm transition-all flex items-center gap-1.5 disabled:opacity-50"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span>{actionLoading ? "Verifying..." : "Verify Buddy"}</span>
                </button>
              )}

              <button
                onClick={() =>
                  handleAction(statusInfo.isSuspended ? "unsuspend" : "suspend")
                }
                disabled={actionLoading}
                className={`px-4 py-2 text-xs font-bold rounded-xl shadow-sm transition-all flex items-center gap-1.5 disabled:opacity-50 ${
                  statusInfo.isSuspended
                    ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                    : "bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200"
                }`}
              >
                {statusInfo.isSuspended ? (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>
                      {actionLoading ? "Processing..." : "Lift Restriction"}
                    </span>
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728"
                      />
                    </svg>
                    <span>
                      {actionLoading ? "Processing..." : "Restrict Buddy"}
                    </span>
                  </>
                )}
              </button>

              <button
                onClick={() => fetchProviderDetails()}
                className="p-2 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-700 transition-colors shadow-sm"
                title="Refresh Details"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </button>
            </div>
          </div>
        </header>

        {/* Page Body Container */}
        <div className="p-4 sm:p-8 max-w-7xl w-full mx-auto space-y-6">
          {/* Notifications */}
          {actionSuccess && (
            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-semibold shadow-sm flex items-center gap-2">
              <svg
                className="w-5 h-5 text-emerald-600 shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{actionSuccess}</span>
            </div>
          )}

          {actionError && (
            <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-sm font-semibold shadow-sm flex items-center gap-2">
              <svg
                className="w-5 h-5 text-rose-600 shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{actionError}</span>
            </div>
          )}

          {/* Section 1: Hero Profile Card */}
          <div className="bg-white rounded-2xl border border-stone-200/80 shadow-sm p-6 sm:p-8">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 pb-6 border-b border-stone-100">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 text-center sm:text-left">
                {/* Circular Avatar */}
                <div className="relative shrink-0">
                  {documents?.profileImage && !imageErrors.profileImage ? (
                    <img
                      src={documents.profileImage}
                      alt={personalInfo.name}
                      className="w-24 h-24 rounded-full object-cover border-4 border-stone-100 shadow-md"
                      onLoad={() => handleImageLoad("profileImage")}
                      onError={() => handleImageError("profileImage")}
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-stone-900 border-4 border-stone-100 shadow-md flex items-center justify-center text-[#F8D346] font-black text-2xl">
                      {getInitials(personalInfo.name)}
                    </div>
                  )}
                  {statusInfo.isVerified && (
                    <div
                      className="absolute bottom-0 right-0 w-7 h-7 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center text-white shadow-sm"
                      title="Verified Identity"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </div>

                <div>
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mb-1.5">
                    <h2 className="text-2xl font-black text-stone-900">
                      {personalInfo.name}
                    </h2>
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
                        statusInfo.status === "ACTIVE"
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : "bg-stone-100 text-stone-600 border border-stone-200"
                      }`}
                    >
                      {statusInfo.status || "ACTIVE"}
                    </span>
                  </div>

                  <p className="text-sm font-semibold text-stone-500 mb-3">
                    Provider ID:{" "}
                    <span className="font-mono text-stone-800 bg-stone-100 px-2 py-0.5 rounded">
                      {id}
                    </span>
                  </p>

                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 text-xs text-stone-600">
                    <span className="flex items-center gap-1 bg-stone-50 px-2.5 py-1 rounded-lg border border-stone-200/60">
                      <svg
                        className="w-3.5 h-3.5 text-stone-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                      {personalInfo.phone || "No Phone"}
                    </span>
                    <span className="flex items-center gap-1 bg-stone-50 px-2.5 py-1 rounded-lg border border-stone-200/60">
                      <svg
                        className="w-3.5 h-3.5 text-stone-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                      {personalInfo.email || "No Email"}
                    </span>
                    <span className="flex items-center gap-1 bg-stone-50 px-2.5 py-1 rounded-lg border border-stone-200/60">
                      <svg
                        className="w-3.5 h-3.5 text-stone-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      {personalInfo.city || "N/A"}
                      {personalInfo.state ? `, ${personalInfo.state}` : ""}
                    </span>
                  </div>
                </div>
              </div>

              {/* Quick Overall Rating Box */}
              <div className="bg-stone-900 text-white rounded-2xl p-4 sm:p-5 w-full lg:w-auto shrink-0 flex items-center justify-between lg:flex-col lg:items-center gap-3 border border-stone-800">
                <div className="flex items-center gap-1.5 text-amber-400">
                  <span className="text-2xl font-black">
                    {statistics.ratings.average || "0.0"}
                  </span>
                  <span className="text-sm font-bold text-stone-400">/ 5.0</span>
                </div>
                <div className="text-right lg:text-center">
                  <div className="text-xs font-bold text-stone-300">
                    {statistics.ratings.totalReviews || 0} Reviews
                  </div>
                  <div className="text-[11px] text-[#F8D346] font-semibold">
                    ⭐ Service Rating
                  </div>
                </div>
              </div>
            </div>

            {/* Demographics & Verification Meta Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 pt-6">
              <div className="bg-stone-50 rounded-xl p-3 border border-stone-200/60">
                <span className="text-[11px] font-bold uppercase tracking-wider text-stone-400 block mb-1">
                  Gender
                </span>
                <span className="text-sm font-bold text-stone-800 capitalize">
                  {personalInfo.gender || "Not specified"}
                </span>
              </div>
              <div className="bg-stone-50 rounded-xl p-3 border border-stone-200/60">
                <span className="text-[11px] font-bold uppercase tracking-wider text-stone-400 block mb-1">
                  Pincode
                </span>
                <span className="text-sm font-bold text-stone-800 font-mono">
                  {personalInfo.pincode || "N/A"}
                </span>
              </div>
              <div className="bg-stone-50 rounded-xl p-3 border border-stone-200/60">
                <span className="text-[11px] font-bold uppercase tracking-wider text-stone-400 block mb-1">
                  Registered
                </span>
                <span className="text-sm font-bold text-stone-800">
                  {personalInfo.createdAt
                    ? new Date(personalInfo.createdAt).toLocaleDateString()
                    : "N/A"}
                </span>
              </div>
              <div className="bg-stone-50 rounded-xl p-3 border border-stone-200/60">
                <span className="text-[11px] font-bold uppercase tracking-wider text-stone-400 block mb-1">
                  KYC Status
                </span>
                <span
                  className={`text-sm font-bold ${
                    statusInfo.isVerified ? "text-emerald-700" : "text-amber-700"
                  }`}
                >
                  {statusInfo.isVerified ? "Approved" : "Pending"}
                </span>
              </div>
              <div className="bg-stone-50 rounded-xl p-3 border border-stone-200/60">
                <span className="text-[11px] font-bold uppercase tracking-wider text-stone-400 block mb-1">
                  Services
                </span>
                <span className="text-sm font-bold text-stone-800">
                  {subServices?.length || 0} Registered
                </span>
              </div>
              <div className="bg-stone-50 rounded-xl p-3 border border-stone-200/60">
                <span className="text-[11px] font-bold uppercase tracking-wider text-stone-400 block mb-1">
                  Restrictions
                </span>
                <span
                  className={`text-sm font-bold ${
                    statusInfo.isSuspended ? "text-rose-700" : "text-emerald-700"
                  }`}
                >
                  {statusInfo.isSuspended ? "Suspended" : "None (Clean)"}
                </span>
              </div>
            </div>
          </div>

          {/* Section 2: 4-Grid Key Financial & Booking KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white p-5 rounded-2xl border border-stone-200/80 shadow-sm relative overflow-hidden group hover:border-[#F8D346] transition-all">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-stone-400 uppercase tracking-wider">
                  Total Bookings
                </span>
                <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              </div>
              <div className="text-2xl font-black text-stone-900">
                {statistics.bookings.total || 0}
              </div>
              <div className="text-xs text-stone-500 mt-1 flex items-center gap-1">
                <span className="font-semibold text-emerald-600">
                  {statistics.bookings.completed || 0} completed
                </span>
                <span>• all-time</span>
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-stone-200/80 shadow-sm relative overflow-hidden group hover:border-[#F8D346] transition-all">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-stone-400 uppercase tracking-wider">
                  Completion Rate
                </span>
                <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
              </div>
              <div className="text-2xl font-black text-emerald-600">
                {statistics.bookings.completionRate || 0}%
              </div>
              <div className="text-xs text-stone-500 mt-1">
                Successful booking ratio
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-stone-200/80 shadow-sm relative overflow-hidden group hover:border-[#F8D346] transition-all">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-stone-400 uppercase tracking-wider">
                  Total Earnings
                </span>
                <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
                  <span className="font-bold text-sm">₹</span>
                </div>
              </div>
              <div className="text-2xl font-black text-stone-900">
                ₹{(statistics.earnings.total || 0).toLocaleString()}
              </div>
              <div className="text-xs text-stone-500 mt-1">
                Net lifetime provider payout
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-stone-200/80 shadow-sm relative overflow-hidden group hover:border-[#F8D346] transition-all">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-stone-400 uppercase tracking-wider">
                  Monthly Run-rate
                </span>
                <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                    />
                  </svg>
                </div>
              </div>
              <div className="text-2xl font-black text-stone-900">
                ₹{(statistics.earnings.monthly || 0).toLocaleString()}
              </div>
              <div className="text-xs text-stone-500 mt-1">
                Avg per job: ₹
                {(statistics.earnings.averagePerBooking || 0).toLocaleString()}
              </div>
            </div>
          </div>

          {/* Section 3: Performance Distribution & Status Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Booking Status Distribution */}
            <div className="lg:col-span-2 bg-white rounded-2xl border border-stone-200/80 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-black text-stone-900 text-base flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#F8D346]"></span>
                  Booking Status Breakdown
                </h3>
                <span className="text-xs font-semibold text-stone-500">
                  {statistics.bookings.total || 0} Total Orders
                </span>
              </div>

              {/* Progress bar visual */}
              {statistics.bookings.total > 0 && (
                <div className="h-3 w-full rounded-full bg-stone-100 flex overflow-hidden mb-6">
                  <div
                    style={{
                      width: `${(statistics.bookings.completed / statistics.bookings.total) * 100}%`,
                    }}
                    className="bg-emerald-500"
                    title={`Completed: ${statistics.bookings.completed}`}
                  />
                  <div
                    style={{
                      width: `${(statistics.bookings.pending / statistics.bookings.total) * 100}%`,
                    }}
                    className="bg-amber-400"
                    title={`Pending: ${statistics.bookings.pending}`}
                  />
                  <div
                    style={{
                      width: `${(statistics.bookings.accepted / statistics.bookings.total) * 100}%`,
                    }}
                    className="bg-blue-500"
                    title={`Accepted: ${statistics.bookings.accepted}`}
                  />
                  <div
                    style={{
                      width: `${(statistics.bookings.cancelled / statistics.bookings.total) * 100}%`,
                    }}
                    className="bg-rose-500"
                    title={`Cancelled: ${statistics.bookings.cancelled}`}
                  />
                  <div
                    style={{
                      width: `${(statistics.bookings.rejected / statistics.bookings.total) * 100}%`,
                    }}
                    className="bg-stone-400"
                    title={`Rejected: ${statistics.bookings.rejected}`}
                  />
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-center">
                  <span className="text-xl font-black text-emerald-700 block">
                    {statistics.bookings.completed || 0}
                  </span>
                  <span className="text-xs font-bold text-emerald-800">
                    Completed
                  </span>
                </div>
                <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-center">
                  <span className="text-xl font-black text-amber-700 block">
                    {statistics.bookings.pending || 0}
                  </span>
                  <span className="text-xs font-bold text-amber-800">
                    Pending
                  </span>
                </div>
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-center">
                  <span className="text-xl font-black text-blue-700 block">
                    {statistics.bookings.accepted || 0}
                  </span>
                  <span className="text-xs font-bold text-blue-800">
                    Accepted
                  </span>
                </div>
                <div className="bg-rose-50 border border-rose-100 rounded-xl p-3 text-center">
                  <span className="text-xl font-black text-rose-700 block">
                    {statistics.bookings.cancelled || 0}
                  </span>
                  <span className="text-xs font-bold text-rose-800">
                    Cancelled
                  </span>
                </div>
                <div className="bg-stone-50 border border-stone-200 rounded-xl p-3 text-center">
                  <span className="text-xl font-black text-stone-700 block">
                    {statistics.bookings.rejected || 0}
                  </span>
                  <span className="text-xs font-bold text-stone-600">
                    Rejected
                  </span>
                </div>
              </div>
            </div>

            {/* Performance Gauges */}
            <div className="bg-white rounded-2xl border border-stone-200/80 p-6 shadow-sm flex flex-col justify-between">
              <div>
                <h3 className="font-black text-stone-900 text-base mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                  Reliability Score
                </h3>
                <div className="flex items-center justify-center my-2">
                  <div className="relative inline-flex items-center justify-center">
                    <svg className="w-32 h-32 transform -rotate-90">
                      <circle
                        cx="64"
                        cy="64"
                        r="50"
                        stroke="#F5F5F4"
                        strokeWidth="10"
                        fill="transparent"
                      />
                      <circle
                        cx="64"
                        cy="64"
                        r="50"
                        stroke="#10B981"
                        strokeWidth="10"
                        strokeDasharray={314.159}
                        strokeDashoffset={
                          314.159 -
                          (314.159 *
                            (statistics.bookings.completionRate || 0)) /
                            100
                        }
                        strokeLinecap="round"
                        fill="transparent"
                        className="transition-all duration-1000 ease-out"
                      />
                    </svg>
                    <div className="absolute flex flex-col items-center">
                      <span className="text-2xl font-black text-stone-900">
                        {statistics.bookings.completionRate || 0}%
                      </span>
                      <span className="text-[10px] font-bold text-stone-400 uppercase">
                        Fulfilled
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-stone-50 rounded-xl p-3 text-xs text-stone-600 border border-stone-200/60 mt-4 flex items-center justify-between">
                <span>Avg Booking Value:</span>
                <span className="font-bold text-stone-900">
                  ₹
                  {(statistics.earnings.averagePerBooking || 0).toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Section 4: Income & Bookings Filter (Financial Intelligence) */}
          <div className="bg-white rounded-2xl border border-stone-200/80 p-6 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-5 border-b border-stone-100">
              <div>
                <h3 className="text-lg font-black text-stone-900 flex items-center gap-2">
                  <span className="p-1.5 rounded-lg bg-stone-900 text-[#F8D346]">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </span>
                  Income & Revenue Breakdown
                </h3>
                <p className="text-xs text-stone-500 mt-0.5">
                  Calculate provider payouts and platform commission for custom
                  ranges
                </p>
              </div>

              {/* Date Filter */}
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="date"
                  value={incomeStartDate}
                  onChange={(e) => setIncomeStartDate(e.target.value)}
                  className="px-3 py-1.5 border border-stone-200 rounded-xl text-xs font-semibold text-stone-700 bg-stone-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#F8D346]"
                />
                <span className="text-stone-400 text-xs font-bold">to</span>
                <input
                  type="date"
                  value={incomeEndDate}
                  onChange={(e) => setIncomeEndDate(e.target.value)}
                  className="px-3 py-1.5 border border-stone-200 rounded-xl text-xs font-semibold text-stone-700 bg-stone-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#F8D346]"
                />
                <button
                  onClick={() =>
                    fetchProviderIncome(incomeStartDate, incomeEndDate)
                  }
                  disabled={providerIncomeLoading}
                  className="px-4 py-1.5 bg-stone-900 hover:bg-stone-800 text-white rounded-xl text-xs font-bold transition-colors shadow-sm disabled:opacity-50"
                >
                  {providerIncomeLoading ? "Calculating..." : "Apply Range"}
                </button>
              </div>
            </div>

            {/* Income Totals */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
              <div className="bg-stone-50 rounded-xl p-4 border border-stone-200/70">
                <div className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-1">
                  Gross Bill Amount
                </div>
                <div className="text-xl font-black text-stone-900">
                  ₹{(providerIncome?.totals?.gross || 0).toLocaleString()}
                </div>
                <div className="text-[11px] text-stone-400 mt-0.5">
                  Total booking invoice
                </div>
              </div>

              <div className="bg-amber-50 rounded-xl p-4 border border-amber-200/70">
                <div className="text-xs font-bold text-amber-800 uppercase tracking-wider mb-1">
                  Admin Commission
                </div>
                <div className="text-xl font-black text-amber-900">
                  ₹
                  {(
                    providerIncome?.totals?.adminCommission || 0
                  ).toLocaleString()}
                </div>
                <div className="text-[11px] text-amber-700 mt-0.5">
                  Platform revenue cut
                </div>
              </div>

              <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200/70">
                <div className="text-xs font-bold text-emerald-800 uppercase tracking-wider mb-1">
                  Provider Share
                </div>
                <div className="text-xl font-black text-emerald-900">
                  ₹
                  {(
                    providerIncome?.totals?.providerEarnings || 0
                  ).toLocaleString()}
                </div>
                <div className="text-[11px] text-emerald-700 mt-0.5">
                  Net provider payout
                </div>
              </div>

              <div className="bg-stone-50 rounded-xl p-4 border border-stone-200/70">
                <div className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-1">
                  Range Jobs
                </div>
                <div className="text-xl font-black text-stone-900">
                  {providerIncome?.totals?.count || 0}
                </div>
                <div className="text-[11px] text-stone-400 mt-0.5">
                  Completed in window
                </div>
              </div>
            </div>

            {/* Income Bookings List */}
            <div className="mt-6">
              <h4 className="text-xs font-bold uppercase tracking-wider text-stone-400 mb-3">
                Transactions in Selected Range
              </h4>
              {providerIncome?.bookings && providerIncome.bookings.length > 0 ? (
                <div className="space-y-2.5">
                  {providerIncome.bookings.slice(0, 10).map((b) => (
                    <div
                      key={b.id}
                      className="p-3.5 bg-stone-50 hover:bg-stone-100/80 rounded-xl border border-stone-200/60 transition-colors flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                    >
                      <div>
                        <div className="font-bold text-sm text-stone-900">
                          {b.service?.name || "Service"} —{" "}
                          <span className="text-stone-600 font-medium">
                            {b.subService?.name || "General"}
                          </span>
                        </div>
                        <div className="text-xs text-stone-500 mt-0.5 flex items-center gap-2">
                          <span>User: {b.user?.name || "Client"}</span>
                          <span>•</span>
                          <span>{b.user?.phone || "N/A"}</span>
                          <span>•</span>
                          <span className="text-stone-400">
                            {b.completedAt
                              ? new Date(b.completedAt).toLocaleString()
                              : "N/A"}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center sm:text-right gap-4">
                        <div>
                          <div className="text-xs text-stone-500">Gross</div>
                          <div className="text-sm font-black text-stone-800">
                            ₹{(b.grossAmount || 0).toLocaleString()}
                          </div>
                        </div>
                        <div className="border-l border-stone-200 pl-4">
                          <div className="text-xs text-emerald-600 font-semibold">
                            Provider Net
                          </div>
                          <div className="text-sm font-black text-emerald-700">
                            ₹{(b.providerEarnings || 0).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {providerIncome.bookings.length > 10 && (
                    <div className="text-center pt-2">
                      <span className="text-xs text-stone-400 font-medium">
                        Showing latest 10 of {providerIncome.totals?.count} job
                        records
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 bg-stone-50 rounded-xl border border-dashed border-stone-200">
                  <p className="text-xs font-semibold text-stone-400">
                    No completed booking income found within this date range
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Section 5: KYC Verification & Official Documents */}
          <div className="bg-white rounded-2xl border border-stone-200/80 p-6 shadow-sm">
            <div className="flex items-center justify-between pb-5 border-b border-stone-100 mb-6">
              <div>
                <h3 className="text-lg font-black text-stone-900 flex items-center gap-2">
                  <span className="p-1.5 rounded-lg bg-stone-900 text-[#F8D346]">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </span>
                  Verification & KYC Dossier
                </h3>
                <p className="text-xs text-stone-500 mt-0.5">
                  Click any document to inspect full-resolution identity proof
                </p>
              </div>

              <button
                onClick={() => fetchProviderDetails()}
                className="px-3 py-1.5 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-xl text-xs font-bold transition-colors flex items-center gap-1.5"
                title="Refresh presigned S3 URLs"
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                <span>Refresh URLs</span>
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                {
                  key: "profileImage",
                  label: "Profile Photo",
                  badge: "Portrait",
                  url: documents?.profileImage,
                },
                {
                  key: "aadharImage",
                  label: "Aadhar Card (Front)",
                  badge: "ID Proof",
                  url: documents?.aadharImage,
                },
                {
                  key: "aadharBackImage",
                  label: "Aadhar Card (Back)",
                  badge: "Address Proof",
                  url: documents?.aadharBackImage,
                },
                {
                  key: "panImage",
                  label: "PAN Card",
                  badge: "Tax ID",
                  url: documents?.panImage,
                },
              ].map((doc) => (
                <div
                  key={doc.key}
                  className="bg-stone-50 rounded-2xl border border-stone-200/80 p-4 flex flex-col justify-between hover:border-stone-400 transition-all group"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-bold text-stone-800">
                      {doc.label}
                    </span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-stone-200 text-stone-700">
                      {doc.badge}
                    </span>
                  </div>

                  {doc.url && !imageErrors[doc.key] ? (
                    <div
                      onClick={() => setFullscreenImg(doc.url)}
                      className="cursor-pointer relative overflow-hidden rounded-xl border border-stone-200 bg-stone-200/60 aspect-video flex items-center justify-center group-hover:shadow-md transition-all"
                    >
                      {imageLoading[doc.key] !== false && (
                        <div className="absolute inset-0 flex items-center justify-center bg-stone-100">
                          <div className="w-5 h-5 border-2 border-[#F8D346] border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                      <img
                        src={doc.url}
                        alt={doc.label}
                        className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ${
                          imageLoading[doc.key] ? "opacity-0" : "opacity-100"
                        }`}
                        onLoad={() => handleImageLoad(doc.key)}
                        onError={() => handleImageError(doc.key)}
                      />
                      <div className="absolute inset-0 bg-stone-950/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="px-3 py-1.5 rounded-lg bg-white/90 text-stone-900 text-xs font-bold flex items-center gap-1.5 shadow-lg">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                            />
                          </svg>
                          View Fullsize
                        </span>
                      </div>
                    </div>
                  ) : imageErrors[doc.key] ? (
                    <div className="aspect-video bg-rose-50 border border-rose-200 rounded-xl p-3 flex flex-col items-center justify-center text-center">
                      <svg
                        className="w-6 h-6 text-rose-500 mb-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4m0 4v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="text-xs font-bold text-rose-700">
                        Link Expired
                      </span>
                      <button
                        onClick={() => fetchProviderDetails()}
                        className="mt-2 px-2.5 py-1 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-[10px] font-bold"
                      >
                        Retry Load
                      </button>
                    </div>
                  ) : (
                    <div className="aspect-video bg-stone-100 border border-dashed border-stone-300 rounded-xl flex flex-col items-center justify-center text-stone-400">
                      <svg
                        className="w-6 h-6 mb-1 text-stone-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <span className="text-[11px] font-semibold">
                        Not Uploaded
                      </span>
                    </div>
                  )}
                  <div className="mt-2 text-right">
                    <span className="text-[11px] text-stone-400">
                      {doc.url ? "Signed S3 Document" : "Pending Upload"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section 6: Registered Services & Skill Catalog */}
          <div className="bg-white rounded-2xl border border-stone-200/80 p-6 shadow-sm">
            <div className="pb-5 border-b border-stone-100 mb-6 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-black text-stone-900 flex items-center gap-2">
                  <span className="p-1.5 rounded-lg bg-stone-900 text-[#F8D346]">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </span>
                  Services & Skill Catalog
                </h3>
                <p className="text-xs text-stone-500 mt-0.5">
                  Authorized trade categories and custom hourly/flat pricing
                </p>
              </div>
              <span className="px-3 py-1 bg-stone-100 text-stone-800 text-xs font-bold rounded-xl">
                {subServices?.length || 0} Listed
              </span>
            </div>

            {subServices && subServices.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {subServices.map((service) => (
                  <div
                    key={service.id}
                    className="p-5 bg-stone-50 rounded-2xl border border-stone-200/80 hover:border-[#F8D346] hover:bg-white transition-all shadow-none hover:shadow-sm"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-bold text-stone-900 text-base">
                        {service.name}
                      </h4>
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
                          service.isActive
                            ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                            : "bg-stone-200 text-stone-600"
                        }`}
                      >
                        {service.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>

                    <div className="space-y-2 text-xs pt-3 border-t border-stone-200/60">
                      <div className="flex items-center justify-between text-stone-600">
                        <span>Experience Level:</span>
                        <span className="font-bold text-stone-900">
                          {service.experience || 0} Years
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-stone-600">
                        <span>Custom Price Rate:</span>
                        <span className="font-black text-emerald-700 text-sm">
                          ₹{service.customPrice || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10 bg-stone-50 rounded-2xl border border-dashed border-stone-200">
                <svg
                  className="w-10 h-10 text-stone-300 mx-auto mb-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
                <p className="text-sm font-bold text-stone-600">
                  No Services Configured
                </p>
                <p className="text-xs text-stone-400 mt-0.5">
                  This provider has not added any trade services to their profile
                  yet.
                </p>
              </div>
            )}
          </div>

          {/* Section 7: Rating Breakdown */}
          <div className="bg-white rounded-2xl border border-stone-200/80 p-6 shadow-sm">
            <div className="pb-5 border-b border-stone-100 mb-6">
              <h3 className="text-lg font-black text-stone-900 flex items-center gap-2">
                <span className="p-1.5 rounded-lg bg-stone-900 text-[#F8D346]">
                  ⭐
                </span>
                Customer Rating Distribution
              </h3>
              <p className="text-xs text-stone-500 mt-0.5">
                Feedback distribution from completed client reservations
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div className="space-y-3">
                {[5, 4, 3, 2, 1].map((stars) => {
                  const count = statistics?.ratings?.distribution?.[stars] || 0;
                  const total = statistics?.ratings?.totalReviews || 0;
                  const pct = total > 0 ? (count / total) * 100 : 0;
                  return (
                    <div key={stars} className="flex items-center gap-3 text-xs">
                      <span className="w-10 font-bold text-stone-700 flex items-center gap-1">
                        {stars}
                        <span className="text-amber-500">★</span>
                      </span>
                      <div className="flex-1 bg-stone-100 rounded-full h-2.5 overflow-hidden">
                        <div
                          className="bg-amber-400 h-2.5 rounded-full transition-all duration-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="w-16 text-right font-semibold text-stone-500">
                        {count} ({pct.toFixed(0)}%)
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="bg-stone-50 rounded-2xl p-6 border border-stone-200/80 text-center flex flex-col items-center justify-center">
                <div className="text-4xl font-black text-stone-900 mb-1">
                  {statistics.ratings.average || "0.0"}
                </div>
                <div className="flex items-center gap-1 text-amber-500 text-lg mb-2">
                  {"★★★★★"}
                </div>
                <div className="text-xs font-bold text-stone-700">
                  Overall Score
                </div>
                <div className="text-[11px] text-stone-400 mt-0.5">
                  Based on {statistics.ratings.totalReviews || 0} client reviews
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Fullscreen Image Lightbox Modal */}
        {fullscreenImg && (
          <div
            className="fixed inset-0 bg-stone-950/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-all"
            onClick={() => setFullscreenImg(null)}
          >
            <div
              className="relative max-h-[90vh] max-w-[90vw] bg-white rounded-2xl p-2 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={fullscreenImg}
                alt="Document Verification Preview"
                className="max-h-[85vh] max-w-full rounded-xl object-contain"
              />
              <button
                className="absolute -top-3 -right-3 bg-stone-900 hover:bg-stone-800 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shadow-lg transition-transform hover:scale-110"
                onClick={() => setFullscreenImg(null)}
              >
                ✕
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ProviderDetailsPage;
